(ns arthur.events.footage
  "Load and freeze ingested footage once, outside the playback loop.

  The frames come from the server by URL since step 9 — see `flow/ingest` — and the
  detector's identity comes from the server too, because it goes into the content
  address of every block this produces."
  (:require [arthur.domain.bring :as bring]
            [arthur.domain.clip :as clip]
            [arthur.domain.span :as span]
            [arthur.events.edit :as edit]
            [arthur.events.ui :as ui]
            [arthur.events.playback :as pb]
            [arthur.flow.detect :as detect]
            [arthur.flow.ingest :as ingest]
            [arthur.flow.measure.interior :as interior]
            [arthur.flow.source :as source]
            [arthur.flow.take :as take]
            [arthur.footage.store :as store]
            [arthur.domain.landmarks :as lm]
            [arthur.fx.http :as http]
            [clojure.string :as string]
            [re-frame.core :as rf]))

(defonce ^:private clock (atom 0))

(defn- mark!
  "Where the time goes, on the console, one line per stage.

  Kept rather than removed after it earned its keep. Progress only paints every
  fourth frame, so a stall near the end looks identical whether the decoder has
  stopped delivering or the work after it is holding the main thread — and those
  two were confused for each other three times before this printed the answer:
  decoding was finished, and `measure-crops` was ten seconds of synchronous
  arithmetic with nothing able to repaint."
  [label]
  (let [now (js/Date.now)
        since (- now @clock)]
    (reset! clock now)
    (js/console.log (str "arthur ⏱ " label "  +" since "ms"))))

(defn- detect-frames!
  "Decode the proxy once, in order, and measure every frame as it goes.

  ONCE AND FORWARD IS A REQUIREMENT, NOT A STYLE. MediaPipe's video mode is a
  tracker whose input stream refuses a timestamp that does not advance, and the
  error it raises is terminal for the landmarker — so there is no re-reading a
  frame and no second pass. That used to be a constraint the frame walk had to be
  careful about; with a decoder it is simply what decoding is.

  The work happens inside `decode!`'s callback, and the promise it returns is the
  backpressure: the decoder does not run ahead of the detector, so a 900-frame
  take does not hold 900 decoded frames at 1440x1920 in memory.

  Only source frames `[start end)` are measured. Decoding still begins at frame
  0, because every frame after the first is coded against the ones before it,
  and it stops at `end`; frames before `start` are decoded and dropped unread."
  [manifest model [start end]]
  (let [[w h]  [(:width manifest) (:height manifest)]
        canvas (.createElement js/document "canvas")
        ctx    (.getContext canvas "2d" #js {:willReadFrequently true})
        fps    (:fps manifest)
        raw    (atom [])
        crops  (atom [])
        inner  (atom [])
        total  end]
    (set! (.-width canvas) w)
    (set! (.-height canvas) h)
    (rf/dispatch [::progress "loading the video…"])
    (-> (ingest/stream! (ingest/stream-url manifest) (:frames manifest))
        (.then
         (fn [stream]
           (ingest/decode!
            (update stream :units subvec 0 end) fps w h
            (fn [i frame]
              (when (>= i start)
                (.drawImage ctx frame 0 0)
                ;; EVERY FACE ON THIS FRAME, each with its own mouth crop taken
                ;; while the frame's pixels are still on the canvas. Which of
                ;; these detections belongs to which subject is not decided here
                ;; — the answer needs the whole take — so all three vectors stay
                ;; in DETECTION ORDER and `detect/tracks` re-keys them afterwards.
                (let [faces (detect/detect! model canvas (ingest/frame-ms fps i))
                      boxes (mapv (fn [face]
                                    (interior/crop (mapv #(nth face %) lm/LIPS-INNER)
                                                   [w h]))
                                  faces)
                      frame-crops (mapv (fn [box]
                                          (when box
                                            {:box box
                                             :data (.-data (.getImageData
                                                            ctx (:x box) (:y box)
                                                            (:w box) (:h box)))}))
                                        boxes)]
                  (swap! raw conj faces)
                  (swap! crops conj frame-crops)
                  ;; MEASURED HERE, NOT IN A SECOND PASS. It is the same work
                  ;; either way, but done after the fact it is ten seconds of
                  ;; synchronous arithmetic with the main thread held and the
                  ;; frame counter frozen on its last value — which reads as the
                  ;; decoder hanging, and was diagnosed as that twice.
                  (swap! inner conj (mapv #(source/measure-crop take/knobs %)
                                          frame-crops))))
              (when (or (zero? i) (zero? (mod (inc i) 4)) (= (inc i) total))
                (rf/dispatch [::progress (if (< i start)
                                           (str "seeking " (inc i) "/" start)
                                           (str "detecting " (- (inc i) start) "/" (- end start)))]))
              ;; Yield, so the status and the transport paint between synchronous
              ;; MediaPipe calls. `decode!` waits on this before feeding more.
              (js/Promise. (fn [done] (js/setTimeout done 0)))))))
        (.then (fn [_]
                 (mark! (str "DECODE FINISHED — " (count @raw) " frames"))
                 (let [slots (detect/tracks @raw)
                       subjects (into {}
                                      (map (fn [[id track]]
                                             [id (assoc (detect/fill-gaps
                                                         (detect/pick @raw track))
                                                        :crops (detect/pick @crops track)
                                                        :interior (detect/pick @inner track)
                                                        :interior-settings take/knobs)]))
                                      slots)]
                   (mark! "fill-gaps")
                   {:subjects subjects
                    :dimensions [w h]
                    ;; Frames where NOBODY was found, which is a fact about the
                    ;; footage. A frame one of two faces is missing from is a gap
                    ;; in that subject's own detection mask and is reported there.
                    :missing (count (remove seq @raw))
                    :first-real (first (keep-indexed (fn [i faces] (when (seq faces) i))
                                                     @raw))}))))))

(defn presence-for
  "Select a subject's masks and express them in its local feature names.
  Unqualified manifest ids refer to the first face, as in single-face footage."
  [manifest subject]
  (not-empty
   (into {} (keep (fn [[id mask]]
                    (when (= (or (namespace id) "face-1") (subs (str subject) 1))
                      [(keyword (name id)) mask])))
         (:presence manifest))))

(defn- build-clip [manifest detector
                   {:keys [subjects dimensions missing first-real] :as source-inputs}]
  (let [[w h] dimensions
        _ (mark! "build-clip: start")
        with-presence (into {}
                            (map (fn [[id inputs]]
                                   [id (assoc inputs :presence (presence-for manifest id))]))
                            subjects)
        frozen (take/footage manifest {:dimensions dimensions
                                       :subjects with-presence
                                       :detector detector})
        _ (mark! "build-clip: freeze")
        built (:clip frozen)
        analysis-id (-> built :analyses keys first)
        source-blocks (source/pack-subjects analysis-id subjects)
        _ (mark! "build-clip: pack source blocks")]
    (assoc (select-keys built [:fps :width :height])

           :clip built :store (:store frozen)
           :source-blocks source-blocks
           :source-inputs (assoc source-inputs :subjects with-presence)
           ;; No cache-buster. The audio is a blob named by the hash of its own
           ;; bytes, so re-extracting gives it a different URL rather than
           ;; overwriting this one — which is what the `?v=` here used to work
           ;; around.
           :audio (ingest/audio-url manifest)
           :label (or (:label manifest) (:source manifest) "footage")
           :footage-id (:id manifest)
           :cid (or (:id manifest) "footage")
           :summary (str (:frames manifest) " frames · " w "×" h " · "
                         (:fps manifest) " fps"
                         (when (> (count subjects) 1)
                           (str " · " (count subjects) " faces"))
                         (when (pos? missing)
                           (str " · " missing " without a face"
                                (when (pos? first-real)
                                 (str " (first found on " (inc first-real) ")"))))))))

(defn- with-default-interior!
  "Backfill one subject's retained interior measurements at the default knobs."
  [analysis subject track]
  (let [frames (count (:crops track))
        key (source/interior-key analysis subject take/knobs frames)]
    (-> (http/GET (str "/api/blocks/" key))
        (.then (fn [block]
                 (assoc track :interior (source/unpack-interior block take/knobs frames)
                        :interior-settings take/knobs
                        :interior-key key)))
        (.catch (fn [error]
                  (if (= 404 (:status (ex-data error))) track (throw error)))))))

(defn saved-source!
  "Restore retained source tracks by analysis id, for regeneration after open.

  An analysis holds one set of blocks per tracked subject, so the count is a
  multiple of `source/roles` rather than equal to it; `source/unpack` reads each
  block's own descriptor to find out whose it is."
  [key dimensions]
  (-> (http/GET (str "/api/analyses/" key))
      (.then (fn [^js analysis]
               (let [keys (array-seq (.-source_blocks analysis))]
                 (when (and (seq keys) (zero? (mod (count keys) (count source/roles))))
                   (-> (js/Promise.all
                        (into-array (map #(http/GET (str "/api/blocks/" %)) keys)))
                       (.then (fn [blocks] (source/unpack blocks dimensions)))
                       (.then (fn [{:keys [subjects]}]
                                (-> (js/Promise.all
                                     (into-array
                                      (map (fn [[id track]]
                                             (.then (with-default-interior! key id track)
                                                    (fn [filled] [id filled])))
                                           subjects)))
                                    (.then (fn [pairs]
                                             {:subjects (into {} (array-seq pairs))
                                              :dimensions dimensions}))))))))))
      (.catch (fn [error]
                (if (= 404 (:status (ex-data error))) nil (throw error))))))

(defn- cached-source! [manifest detector]
  (saved-source! (:id (take/analysis-for manifest detector))
                 [(:width manifest) (:height manifest)]))

(defn measure-one!
  "One subject's crops, ONE PER EVENT-LOOP TURN."
  [settings track on-step]
  (if (or (:interior track) (not (:crops track)))
    (js/Promise.resolve track)
    (let [crops (:crops track)
          total (count crops)
          interior (atom [])]
      (js/Promise.
       (fn [resolve reject]
         (letfn [(step [i]
                   (if (= i total)
                     (resolve (assoc track :interior @interior
                                     :interior-settings settings))
                     (try
                       (swap! interior conj (source/measure-crop settings (nth crops i)))
                       (when on-step (on-step (inc i) total))
                       (js/setTimeout #(step (inc i)) 0)
                       (catch :default error (reject error)))))]
           (step 0)))))))

(defn measure-crops!
  "Measure every retained crop's interior, one subject after another and one
  crop per event-loop turn.

  Done in a tight loop instead, it is ten seconds of synchronous arithmetic with
  the main thread held and the frame counter frozen on its last value — which
  reads as the decoder hanging, and was diagnosed as that twice.

  Two callers, and the only difference between them is who is waiting: opening an
  older analysis that predates the interior block backfills it behind a progress
  line, and a knob drag that needs the pixels measured at new settings does the
  same work with nobody watching, so it passes no `on-step`. The settings ride
  back on each track, because a measurement and the knobs it was taken at are one
  fact — `source/pack` will not address an interior block without them."
  [settings track on-step]
  (reduce (fn [chain [id one]]
            (.then chain
                   (fn [acc]
                     (.then (measure-one! settings one on-step)
                            (fn [measured] (assoc-in acc [:subjects id] measured))))))
          (js/Promise.resolve track)
          (:subjects track)))

(defn- analyse!
  "Promise of source frames `[start end)` of footage `footage-id`, detected,
  measured and frozen — `{:clip :store ...}` as `build-clip` makes it, with the
  footage reading as though it were only those frames. A saved analysis of
  exactly that range is reused instead of detecting again."
  [footage-id [start end]]
  (-> (js/Promise.all #js [(ingest/manifest! footage-id) (ingest/detector!)])
      (.then (fn [[full detector]]
               (let [manifest (ingest/slice full start end)]
                 (reset! clock (js/Date.now))
                 (rf/dispatch [::progress "looking for saved analysis…"])
                 (-> (cached-source! manifest detector)
                     (.then (fn [track]
                              (if track
                                (do (rf/dispatch [::progress "reusing saved analysis…"])
                                    (-> (measure-crops!
                                         take/knobs track
                                         (fn [done total]
                                           (when (or (= 1 done) (zero? (mod done 4))
                                                     (= done total))
                                             (rf/dispatch
                                              [::progress (str "measuring " done "/" total)]))))
                                        (.then #(build-clip manifest detector %))))
                                (do (rf/dispatch [::progress "loading MediaPipe…"])
                                    (-> (detect/landmarker!)
                                        (.then (fn [model]
                                                 (mark! "MediaPipe ready")
                                                 (rf/dispatch [::progress "opening the video…"])
                                                 (detect-frames! full model [start end])))
                                        (.then #(build-clip manifest detector %)))))))))))))

(rf/reg-fx
 ::convert!
 (fn [{:keys [footage-id range] :as request}]
   (-> (analyse! footage-id range)
       (.then (fn [built]
                (mark! "build-clip: done")
                (rf/dispatch [::converted request built])))
       (.catch (fn [error]
                 (js/console.error error)
                 ;; A run that ended badly may have ended on a MediaPipe graph
                 ;; error, and a landmarker that has hit one throws the same error
                 ;; for the rest of the page's life. Retrying has to get a new one.
                 (detect/discard!)
                 (rf/dispatch [::failed (or (ex-message error) (.-message error) (str error))]))))))

(rf/reg-fx
 ::list!
 (fn [_]
   (-> (js/Promise.all #js [(ingest/available!)
                            (.then (http/GET "/api/sounds")
                                   #(:sounds (js->clj % :keywordize-keys true)))
                            (.then (http/GET "/api/images")
                                   #(:images (js->clj % :keywordize-keys true)))])
       (.then (fn [[footage sounds images]] (rf/dispatch [::listed footage sounds images])))
       (.catch (fn [error]
                 (rf/dispatch [::failed (or (ex-message error) (str error))]))))))

(defn- poll-extraction! [key]
  (-> (http/GET (str "/api/extractions/" key))
      (.then (fn [^js job]
               (case (.-state job)
                 "done" (rf/dispatch [::uploaded (.-footage job)])
                 "failed" (rf/dispatch [::failed (.-error job)])
                 (do (rf/dispatch [::progress
                                   (str "extracting " (.-progress job) "%")])
                     (js/setTimeout #(poll-extraction! key) 800)))))
      (.catch (fn [error]
                (rf/dispatch [::failed (or (ex-message error) (str error))])))))

(defn- sending
  "A progress callback that puts the whole percentage sent in the status line.

  ONLY WHEN THE PERCENTAGE MOVES. The browser fires upload progress as often as
  it pleases and a status line has something new to say a hundred times at most,
  and each dispatch here re-renders the pane.

  Worth having at all because the transfer is the longest part of an import on
  anything but a local server, and it was the part with no number on it:
  `uploading video…` sat unchanged from the first byte to the last, however many
  there were, and only the extraction that followed it ever counted. See
  `arthur.fx.http/POST-form`."
  [label]
  (let [reported (atom -1)]
    (fn [fraction]
      (let [percent (js/Math.round (* 100 fraction))]
        (when (not= percent @reported)
          (reset! reported percent)
          (rf/dispatch [::progress (str label " " percent "%")]))))))

(rf/reg-fx
 ::upload!
 (fn [file]
   (let [form (js/FormData.)]
     (.append form "file" file)
     (-> (http/POST-form "/api/sources" form (sending "uploading video…"))
         (.then (fn [^js source]
                  (rf/dispatch [::progress "queued for extraction…"])
                  (http/POST "/api/extractions" #js {:source (.-id source)
                                                      :settings #js {}})))
         (.then (fn [^js job] (poll-extraction! (.-key job))))
         (.catch (fn [error]
                   (rf/dispatch [::failed (or (ex-message error) (str error))])))))))

(rf/reg-fx
 ::upload-sound!
 (fn [file]
   (let [form (js/FormData.)]
     (.append form "file" file)
     (-> (http/POST-form "/api/sounds" form (sending "uploading sound…"))
         (.then (fn [^js sound] (rf/dispatch [::uploaded (.-id sound) "sound imported"])))
         (.catch (fn [error]
                   (rf/dispatch [::failed (or (ex-message error) (str error))])))))))

(rf/reg-fx
 ::upload-image!
 (fn [file]
   (let [form (js/FormData.)]
     (.append form "file" file)
     (-> (http/POST-form "/api/images" form (sending "uploading image…"))
         (.then (fn [^js image] (rf/dispatch [::uploaded (.-id image) "image imported"])))
         (.catch (fn [error]
                   (rf/dispatch [::failed (or (ex-message error) (str error))])))))))

(rf/reg-event-fx
 ::upload
 (fn [{:keys [db]} [_ ^js file]]
   ;; By type, and by name for a browser that leaves the type empty.
   (let [kind (when file
                (cond
                  (or (string/starts-with? (.-type file) "audio/")
                      (re-find #"(?i)\.(mp3|wav|aiff?|flac|ogg|m4a|aac)$" (.-name file)))
                  :sound
                  (or (string/starts-with? (.-type file) "image/")
                      (re-find #"(?i)\.(png|jpe?g|gif|webp)$" (.-name file)))
                  :image
                  :else :video))]
     (if (or (nil? file) (get-in db [:footage :loading?]))
       {}
       {:db (update db :footage merge {:loading? true
                                       :status (str "uploading " (name kind) "…")})
        (case kind :sound ::upload-sound! :image ::upload-image! ::upload!) file}))))

(rf/reg-event-fx
 ::uploaded
 (fn [{:keys [db]} [_ id status]]
   ;; Into the pool, and no further. An upload is media for this project; turning
   ;; it into a symbol or a sound is a separate decision — which frames, what
   ;; name, where — made by dropping it where it should go.
   {:db (update db :footage #(-> %
                                 (merge {:loading? false :status (or status "video extracted")})
                                 (cond-> (not status) (assoc :chosen id))
                                 (update :uploaded (fnil conj #{}) id)))
    :dispatch [::refresh]}))

(rf/reg-event-fx
 ::refresh
 (fn [_ _] {::list! nil}))

(rf/reg-event-db
 ::listed
 (fn [db [_ footage sounds images]]
   (update db :footage merge
           (cond-> {:available (vec footage)
                    :sounds (vec sounds)
                    :images (vec images)
                    :chosen (or (:chosen (:footage db)) (:id (first footage)))}
             (empty? footage) (assoc :status "upload a video to begin")))))

(rf/reg-event-db
 ::choose
 (fn [db [_ id]]
   (-> db
       (assoc-in [:footage :chosen] id)
       (ui/selected [:footage id]))))

;; ---------------------------------------------------------------------------
;; renaming an asset
;;
;; A SERVER WRITE, NOT A DOCUMENT EDIT, and so not on the undo list. Footage and
;; sounds live beside projects rather than inside one — the pool's ALL ASSETS
;; folder is exactly that — so a label is shared by every project that uses the
;; row, and undoing an edit to this document must not reach out and rename
;; something another one is showing.
;;
;; Written through optimistically. The lists in app-db are what the pool draws
;; from; waiting for the round trip would leave the old name under the cursor for
;; as long as the request takes, and the failure is visible and recoverable —
;; `::failed` says so, and `::refresh` puts back whatever the server actually
;; holds.

(defn- relabelled
  "Replace one row's `:label` in a list held by id."
  [rows id label]
  (mapv #(cond-> % (= id (:id %)) (assoc :label label)) rows))

(rf/reg-fx
 ::relabel!
 (fn [{:keys [url label]}]
   (-> (http/PATCH url #js {:label label})
       (.then (fn [_]
                ;; Only a CLEARED label needs the answer. The server's fallback
                ;; is the name the file was uploaded under, which this client
                ;; cannot reconstruct — footage falls back to its source and a
                ;; sound to its filename — so the one case the optimistic write
                ;; cannot guess is the one case that re-lists.
                (when (empty? label) (rf/dispatch [::refresh]))))
       (.catch (fn [error]
                 (rf/dispatch [::failed (or (ex-message error) (str error))])
                 (rf/dispatch [::refresh]))))))

(rf/reg-event-fx
 ::relabel
 (fn [{:keys [db]} [_ kind id value]]
   ;; `kind` is `:footage`, `:sound` or `:image`: resources with one field between
   ;; them, and one event rather than two that differ by a path and a URL.
   (let [label (string/trim (str value))
         [key url] (case kind
                     :footage [:available (str "/api/footage/" id)]
                     :sound   [:sounds (str "/api/sounds/" id)]
                     :image   [:images (str "/api/images/" id)]
                     [nil nil])]
     (if (or (nil? id) (nil? key))
       {}
       {:db (cond-> db
              (seq label) (update-in [:footage key] relabelled id label))
        ::relabel! {:url url :label label}}))))

(rf/reg-event-db
 ::progress
 (fn [db [_ message]] (assoc-in db [:footage :status] message)))

(rf/reg-event-db
 ::failed
 (fn [db [_ message]]
   (assoc db :footage (assoc (:footage db)
                            :loading? false :status (str "footage failed: " message)))))


;; ---------------------------------------------------------------------------
;; footage -> a symbol
;;
;; Dropping a video asks first. `[:ui :convert]` is the question — which footage,
;; which of its frames, what to call the result — and where the answer will be
;; placed: `:host`, `:frame` and `:point` are the drop's, captured when it happened
;; so that switching tabs while detection runs does not move where it lands.

(rf/reg-event-db
 ::ask-convert
 (fn [db [_ {:keys [frames label] :as footage} frame point target]]
   (assoc-in db [:ui :convert]
             (merge (select-keys footage [:id :label :frames :fps :video :width :height])
                    {:range [0 frames]
                     :name (string/replace (str label) #"\.[^.]*$" "")
                     :host (get-in db [:ui :open]) :frame frame :point point
                     :target target}))))

(rf/reg-event-db
 ::convert-set
 (fn [db [_ k v]] (assoc-in db [:ui :convert k] v)))

(rf/reg-event-db
 ::convert-cancel
 (fn [db _]
   (if (get-in db [:footage :loading?]) db (update db :ui dissoc :convert))))

(rf/reg-event-fx
 ::convert
 (fn [{:keys [db]} _]
   (let [{:keys [id range] :as request} (get-in db [:ui :convert])]
     (if (or (nil? request) (get-in db [:footage :loading?]))
       {}
       {:db (update db :footage merge {:loading? true :status "starting…"})
        ::pb/pause! nil
        ::convert! {:footage-id id :range range :request request}}))))

(rf/reg-event-fx
 ::convert-tracing
 ;; The same question answered the other way: the chosen frames become a tracing
 ;; layer to draw over, with nothing detected and nothing measured. Its frames
 ;; are the footage's own and its rate the footage's, so it plays at the speed it
 ;; was filmed in a project at any rate.
 (fn [{:keys [db]} _]
   (let [{:keys [id range fps width height name frame point target] :as request}
         (get-in db [:ui :convert])]
     (if (or (nil? request) (get-in db [:footage :loading?]))
       {}
       {:db (update db :ui dissoc :convert)
        :dispatch [::ui/drop-tracing
                   {:name name :type :trace
                    :media {:footage id :range range}
                    :frames (- (second range) (first range)) :fps fps
                    :width width :height height :nodes {}}
                   frame point target]}))))

(rf/reg-event-fx
 ::converted
 ;; A TAKE IS A CLIP LIKE ANY OTHER, placed into whichever symbol the drop names
 ;; — see `ui/drop-destination` — rather than into a container invented for it.
 (fn [{:keys [db]} [_ {{:keys [name frame point range target]} :request footage-id :footage-id}
                    built]]
   (let [entry (store/entry (:clip/current db))
         uuid  (random-uuid)
         fps   (get-in db [:clip :fps])
         {:keys [clip sid subject-ids]}
         (bring/take (:clip entry) (:clip built) name footage-id range)
         st    (merge (:store entry) (:store built))
         imported-frames (clip/frames clip sid)
         source-fps (get-in built [:clip :fps])
         where (if point
                 (ui/creation-destination db clip st frame)
                 (ui/drop-destination db clip st frame target))
         point (ui/destination-point where point)
         result (if (:refused where)
                  where
                  (span/place-symbol (:clip where) st (:sid where)
                                     uuid sid (:at where)
                                     {:extent :grow-symbol :point point
                                      :remainder-id (random-uuid)}))]
     (if-let [why (or (:refused where) (:refused result))]
       {:db (-> db
                (update :ui dissoc :convert)
                (update :footage merge {:loading? false :status why}))}
       (let [db (edit/edit-entry
                 db
                 #(let [analysis-id (-> built :clip :analyses keys first)
                        remap-subjects
                        (fn [inputs]
                          (update inputs :subjects
                                  (fn [subjects]
                                    (into {} (map (fn [[old new]] [new (get subjects old)]))
                                          subject-ids))))]
                    (-> (assoc % :clip (:clip result) :store st)
                        (update-in [:sources analysis-id]
                                   (fn [source]
                                     {:footage-id (:footage-id built)
                                      :source-blocks (:source-blocks built)
                                      :source-inputs
                                      (update (remap-subjects (:source-inputs built))
                                              :subjects merge
                                              (get-in source [:source-inputs :subjects]))})))))]
         {:db (-> db
                  (update :ui dissoc :convert)
                  (ui/selected
                   [:node (:sid where) uuid (conj (vec (:path where)) uuid)])
                  (update :footage merge
                          {:loading? false
                           :status (str "made " name " · " imported-frames " frames at " fps " fps"
                                        (when (not= fps source-fps)
                                          (str " · sampled from " source-fps " fps")))}))
          :dispatch [::pb/refresh-clock]})))))
